// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import moment, {Moment, MomentInput} from 'moment';
// which can fail type checking if the date util mixes these with moment-timezone instead
import momentTimezone from 'moment-timezone';

import {Translatable} from 'common/i18n';
import type {Section} from 'common/components/items-menu/types';
import {CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT, Timezone} from 'common/constants';
import {FEATURE_GATES_UNLIMITED} from 'common/report/constants';
import {pluralize} from 'common/util/string';
import {
  assertIsStandardTimeUnit,
  clampDateRange,
  formatDate,
  getCurrentDate,
  getCurrentMoment,
  isTimeZero,
  parseDate,
  parseTimestamp,
} from 'common/util/date';
import {TimePeriodSettings} from 'common/report/stores/types';

import {
  DATE_RANGE_TYPE_DISPLAY_NAMES,
  END_OF_TODAY,
  END_OF_YESTERDAY,
  NOW,
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES,
  PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS,
  PresetDateRange,
  START_OF_MONTH,
  START_OF_QUARTER,
  START_OF_TODAY,
  START_OF_WEEK,
  START_OF_YEAR,
  START_OF_YESTERDAY,
  TODAY,
  X_TO_DATE,
} from './presets';
import {
  ApiDateRange,
  BaseDate,
  BookmarkDateRange,
  CalendarDateRange,
  DateRangeType,
  RelativeDate,
  TimeUnit,
} from './types';

export const SPECIAL_DATE_RESOLVERS = {
  [START_OF_YESTERDAY]: (asOfDate?: Date) =>
    (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).subtract(1, `days`).startOf(`day`),
  [END_OF_YESTERDAY]: (asOfDate?: Date) =>
    (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).subtract(1, `days`).endOf(`day`),
  [START_OF_TODAY]: (asOfDate?: Date) =>
    (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).startOf(`day`),
  [END_OF_TODAY]: (asOfDate?: Date) => (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).endOf(`day`),
  [TODAY]: (asOfDate?: Date) => (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).startOf(`day`),
  [NOW]: (asOfDate?: Date) => (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})),
  [START_OF_WEEK]: (asOfDate?: Date, timePeriodSettings?: TimePeriodSettings) => {
    const curMoment = asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true});
    // Adjust ISO weekday to be 0-indexed to match timePeriodSettings.weekStartDay.
    const curMomentWeekday = curMoment.isoWeekday() - 1;
    const weekStartDay = timePeriodSettings?.weekStartDay ?? 0;
    return curMomentWeekday >= weekStartDay
      ? curMoment.subtract(curMomentWeekday - weekStartDay, `day`)
      : curMoment.subtract(7 - curMomentWeekday - weekStartDay, `day`);
  },
  [START_OF_MONTH]: (asOfDate?: Date, _?: TimePeriodSettings) =>
    (asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true})).startOf(`month`),
  [START_OF_QUARTER]: (asOfDate?: Date, timePeriodSettings?: TimePeriodSettings) => {
    const curMoment = asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true});
    const quarterStartMonth = timePeriodSettings?.quarterStartMonth ?? 0;

    // Both moment and TimePeriodSettings use a 0-indexed month from 0-11.
    const curMonth = curMoment.month();
    const curYear = curMoment.year();
    let curQuarterMonth: number;
    let curQuarterYear: number;
    let quarterNum: number;
    if (curMonth >= quarterStartMonth) {
      quarterNum = Math.floor((curMonth - quarterStartMonth) / 3);
      curQuarterYear = curYear;
      curQuarterMonth = quarterStartMonth + quarterNum * 3;
    } else {
      quarterNum = Math.floor((12 - quarterStartMonth + curMonth) / 3);
      curQuarterMonth = (quarterStartMonth + quarterNum * 3) % 12;
      curQuarterYear = curQuarterMonth <= curMonth ? curYear : curYear - 1;
    }

    return curMoment.year(curQuarterYear).month(curQuarterMonth).date(1);
  },
  [START_OF_YEAR]: (asOfDate?: Date, timePeriodSettings?: TimePeriodSettings) => {
    const curMoment = asOfDate ? moment(asOfDate) : getCurrentMoment({localized: true});
    const quarterStartMonth = timePeriodSettings?.quarterStartMonth ?? 0;
    const yearStart = quarterStartMonth + 1 <= curMoment.month() ? curMoment.year() : curMoment.year() - 1;
    return curMoment.year(yearStart).month(quarterStartMonth).date(1);
  },
};

export interface DateRangeAttributes {
  type: DateRangeType;
  from?: BaseDate;
  to?: BaseDate;
  window?: RelativeDate;
  rolling?: boolean;
}

export interface PresetOption {
  label: Translatable;
  shortLabel: Translatable;
  value: DateRangeAttributes;
}

export class DateRange implements DateRangeAttributes {
  public static get presets(): PresetDateRange[] {
    return [];
  }

  public static getPresetOption(preset: PresetDateRange): PresetOption {
    return {
      label: PRESET_DATE_RANGE_DISPLAY_NAMES[preset],
      shortLabel: PRESET_DATE_RANGE_SHORT_DISPLAY_NAMES[preset],
      value: PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[preset],
    };
  }

  public static getPresetOptions(): PresetOption[] {
    return this.presets.map(DateRange.getPresetOption);
  }

  public static convertAbsoluteDateToCalendarDate(date: string, dateFormat?: string): string {
    return (SPECIAL_DATE_RESOLVERS[date] || (() => moment(date)))().format(dateFormat || CALENDAR_DATE_FORMAT);
  }

  /**
   * Returns a moment object for the given date string.
   *
   * @param {string} date - Date string to convert to a moment object.
   * @param {string} projectTimezone - Timezone of the project. May be null if project context hasn't yet been initialized.
   * @param {Moment} asOfDate - Moment for the current time factoring in projectAsOfDate override, if one exists.
   * @param {TimePeriodSettings} timePeriodSettings - Optional settings for the time period.
   *
   * @returns {number} The number of hours in the time clause.
   */
  public static convertAbsoluteDateToMoment(
    date: string,
    projectTimezone: Timezone,
    asOfDate: Moment,
    timePeriodSettings?: TimePeriodSettings,
  ): Moment {
    if (projectTimezone) {
      // Add the timezone to asOfDate just in case it wasn't correctly localize to project timezone.
      asOfDate = momentTimezone.tz(asOfDate, projectTimezone as string);
    }
    if (SPECIAL_DATE_RESOLVERS[date]) {
      return SPECIAL_DATE_RESOLVERS[date](asOfDate, timePeriodSettings);
    } else if (projectTimezone) {
      return momentTimezone.tz(date, projectTimezone as string);
    }
    return moment(date);
  }

  public static convertAbsoluteDateToCalendarDateAsOf(
    date: string,
    asOfDate: Date | Moment,
    timePeriodSettings?: TimePeriodSettings,
    dateFormat?: string,
  ): string {
    if (X_TO_DATE.includes(date)) {
      return SPECIAL_DATE_RESOLVERS[date](asOfDate, timePeriodSettings).format(dateFormat || CALENDAR_DATE_FORMAT);
    }
    return (SPECIAL_DATE_RESOLVERS[date] || ((aod) => moment(date || aod)))(asOfDate).format(
      dateFormat || CALENDAR_DATE_FORMAT,
    );
  }

  public static convertCalendarDateToRelativeDate(dateStr: string): RelativeDate {
    if (!dateStr) {
      return null;
    }

    const currMoment: moment.Moment = getCurrentMoment({localized: true}).startOf(`day`);
    const calendarMoment = moment(dateStr, CALENDAR_DATE_FORMAT).startOf(`day`);
    const daysDiff = currMoment.diff(calendarMoment, `days`);
    return {
      unit: TimeUnit.Day,
      value: daysDiff,
    };
  }

  public static convertRelativeDateToCalendarDate(relativeDate: RelativeDate): string {
    return this.convertRelativeDateToCalendarDateAsOf(relativeDate, getCurrentDate());
  }

  public static convertRelativeDateToCalendarDateAsOf(
    relativeDate: RelativeDate,
    asOfDate: Date | Moment,
    dateFormat?: string,
  ): string {
    if (
      !relativeDate ||
      !(
        [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Year] as TimeUnit[]
      ).includes(relativeDate.unit)
    ) {
      return null;
    }

    /**
     * query-api considers 1 month = 30 days and 1 year = 365 days, so we parse dates to make labels, tooltips and other UI elements consistent.
     */
    if (relativeDate.unit === TimeUnit.Month) {
      relativeDate = {
        unit: TimeUnit.Day,
        value: relativeDate.value * 30,
      };
    } else if (relativeDate.unit === TimeUnit.Year) {
      relativeDate = {
        unit: TimeUnit.Day,
        value: relativeDate.value * 365,
      };
    }

    assertIsStandardTimeUnit(relativeDate.unit);
    return moment(asOfDate)
      .subtract(relativeDate.value, relativeDate.unit)
      .format(dateFormat || CALENDAR_DATE_FORMAT);
  }

  public static convertBaseDateToCalendarDate(baseDate?: BaseDate): string | null {
    if (!baseDate) {
      return null;
    }
    if (DateRange.isRelativeDate(baseDate)) {
      return DateRange.convertRelativeDateToCalendarDate(baseDate);
    }
    return baseDate;
  }

  public static isRelativeDate(date: BaseDate): date is RelativeDate {
    return Object.prototype.hasOwnProperty.call(date ?? {}, `unit`);
  }

  public static fromBookmarkFormat(bookmarkDateRange: BookmarkDateRange): DateRange {
    const {to_date: toDate, from_date: fromDate, type} = bookmarkDateRange;
    const attrs: DateRangeAttributes = {type};
    switch (type) {
      case DateRangeType.RelativeAfter: {
        attrs.window = fromDate as RelativeDate;
        break;
      }
      case DateRangeType.RelativeBefore: {
        attrs.window = toDate as RelativeDate;
        break;
      }
      case DateRangeType.Between:
      case DateRangeType.On: {
        if (this.isRelativeDate(fromDate)) {
          attrs.rolling = true;
        }
        attrs.from = fromDate;
        attrs.to = toDate;
        break;
      }
      case DateRangeType.Since: {
        attrs.from = fromDate;
        break;
      }
      case DateRangeType.StartsOn:
      case DateRangeType.EndsOn:
        attrs.from = fromDate;
        attrs.to = toDate;
        break;
      default: {
        return null;
      }
    }

    return new DateRange(attrs);
  }

  /**
   * Checks the input dateRangeAttrs to see if it has hour/minute granularity for Between, Since, or
   * Last date ranges.
   *
   * @param {DateRangeAttributes} dateRangeAttrs
   * @returns true if the dateRangeAttrs has hour/minute granularity specified, false otherwise.
   */
  public static hasSubDayGranularity(dateRangeAttrs: Partial<DateRangeAttributes> | undefined): boolean {
    if (!dateRangeAttrs) {
      return false;
    }
    switch (dateRangeAttrs.type) {
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter:
      case DateRangeType.RelativeFuture:
        return dateRangeAttrs.window.unit === TimeUnit.Hour || dateRangeAttrs.window.unit === TimeUnit.Minute;
      case DateRangeType.Between:
      case DateRangeType.NotBetween:
      case DateRangeType.On:
      case DateRangeType.NotOn: {
        const fromTimestamp: moment.Moment = parseTimestamp(String(dateRangeAttrs.from));
        const toTimestamp: moment.Moment = parseTimestamp(String(dateRangeAttrs.to));
        return (
          (fromTimestamp.isValid() && !isTimeZero(fromTimestamp)) || (toTimestamp.isValid() && !isTimeZero(toTimestamp))
        );
      }
      case DateRangeType.Since: {
        const sinceFromTimestamp: moment.Moment = parseTimestamp(String(dateRangeAttrs.from));
        return sinceFromTimestamp.isValid() && !isTimeZero(sinceFromTimestamp);
      }
      default:
        throw new Error(
          `"date-range" attribute contains DateRange info: '${JSON.stringify(
            dateRangeAttrs,
          )}' with unsupported DateRangeType: '${dateRangeAttrs.type}'`,
        );
    }
  }

  public declare type: DateRangeType;
  public declare from?: BaseDate;
  public declare to?: BaseDate;
  public declare window?: RelativeDate;
  public declare rolling?: boolean;

  constructor(attributes: Partial<DateRangeAttributes> = {}) {
    attributes = attributes ?? {};
    this.type = attributes.type ?? this.getDefaultType();

    switch (this.type) {
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter:
      case DateRangeType.RelativeBefore: {
        this.window = attributes.window ?? this.getDefaultWindow();
        break;
      }
      case DateRangeType.EndsOn:
      case DateRangeType.StartsOn:
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Before:
      case DateRangeType.Between:
      case DateRangeType.NotBetween:
      case DateRangeType.Since: {
        attributes.to && (this.to = attributes.to);
        attributes.from && (this.from = attributes.from);
        if (isBoolean(attributes.rolling)) {
          this.rolling = attributes.rolling;
        }
        break;
      }
      default: {
        break;
      }
    }
  }

  /**
   * @deprecated
   *
   * NOTE: You probably want to use getCalendarDatesAsOf and pass the projectAsOfDate since that's needed to correctly
   * compute the date range when working with relative dates. There are still a few places that use this function
   * that should be updated to use getCalendarDatesAsOf. It would be even better if the projectAsOfDate was passed
   * when constructing the DateRange object since it's really part of the date range definition and there are some
   * other methods in DateRange that depend on getCalendarDatesAsOf. But it could take some time to find/update all
   * the places where DateRange is constructed.
   */
  public getCalendarDates(dateFormat = CALENDAR_DATE_FORMAT): CalendarDateRange {
    return this.getCalendarDatesAsOf(null, dateFormat);
  }

  public getCalendarDatesAsOf(asOfDateStr?: string, dateFormat?: string): CalendarDateRange;

  public getCalendarDatesAsOf(asOfDateStr?: string, dateFormat?: string, timezone?: Timezone): CalendarDateRange;

  public getCalendarDatesAsOf(
    asOfDateStr?: string,
    dateFormat?: string,
    timezone?: Timezone,
    timePeriodSettings?: TimePeriodSettings,
  ): CalendarDateRange;

  /*
    returns from and to in `day` format anchored around `today` in a timezone. Since queries are done in a
    query timezone, `today` should be in Query timezone. For now, this is optional, but we will do a refactor
    such that timezone is a required argument (@jun)
  */
  public getCalendarDatesAsOf(
    asOfDateStr?: string,
    dateFormat?: string,
    timezone?: Timezone,
    timePeriodSettings?: TimePeriodSettings,
  ): CalendarDateRange {
    const asOfDate = asOfDateStr ? parseDate(asOfDateStr) : getCurrentMoment({timezone});
    const dateGranularity = this.hasSubDayGranularity() ? `minute` : `day`;
    let fromMoment;
    let toMoment;
    if (this.isRelativeWindow()) {
      const absoluteDateString = DateRange.convertRelativeDateToCalendarDateAsOf(this.window, asOfDate, dateFormat);
      fromMoment = moment(absoluteDateString);
      toMoment = moment(asOfDate).startOf(dateGranularity);
      // handle future relative windows
      if (this.type === DateRangeType.RelativeFuture) {
        fromMoment = moment(asOfDate).startOf(dateGranularity);
        toMoment = moment.utc(
          DateRange.convertRelativeDateToCalendarDateAsOf(
            {
              ...this.window,
              value: this.window.value * -1,
            },
            asOfDate,
            dateFormat,
          ),
        );
      }
    } else if (this.rolling) {
      fromMoment = moment.utc(
        DateRange.convertRelativeDateToCalendarDateAsOf(this.from as RelativeDate, asOfDate, dateFormat),
      );
      toMoment = moment.utc(
        DateRange.convertRelativeDateToCalendarDateAsOf(this.to as RelativeDate, asOfDate, dateFormat),
      );
    } else {
      fromMoment = moment.utc(
        DateRange.convertAbsoluteDateToCalendarDateAsOf(this.from as string, asOfDate, timePeriodSettings, dateFormat),
      );
      toMoment = moment.utc(
        DateRange.convertAbsoluteDateToCalendarDateAsOf(this.to as string, asOfDate, timePeriodSettings, dateFormat),
      );
    }
    return {
      from: fromMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
      to: toMoment.format(dateFormat || CALENDAR_DATE_FORMAT),
    };
  }

  public getIntervalDays(asOfDateStr: string = null): number {
    const {to, from} = this.getCalendarDatesAsOf(asOfDateStr, CALENDAR_DATE_FORMAT);

    return moment(to).diff(moment(from), TimeUnit.Day);
  }

  public canUseRolling(): boolean {
    return false;
  }

  public getBookmarkFormat(): BookmarkDateRange {
    const bookmarkDateRange: Partial<BookmarkDateRange> = {type: this.type};
    switch (this.type) {
      case DateRangeType.RelativeAfter:
      case DateRangeType.RelativeFuture: {
        bookmarkDateRange.from_date = this.window;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      case DateRangeType.RelativeBefore: {
        bookmarkDateRange.to_date = this.window;
        break;
      }
      case DateRangeType.StartsOn:
      case DateRangeType.EndsOn:
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = this.to;
        break;
      }
      case DateRangeType.Since: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      default: {
        return null;
      }
    }

    return bookmarkDateRange as BookmarkDateRange;
  }

  public getDefaultType(): DateRangeType {
    return DateRangeType.RelativeAfter;
  }

  public getDefaultWindow(): RelativeDate {
    return {
      unit: TimeUnit.Day,
      value: 7,
    };
  }

  public getDateRangeTypeOptions(): DateRangeType[] {
    return [DateRangeType.RelativeAfter, DateRangeType.Between, DateRangeType.Since, DateRangeType.On];
  }

  public getTimeUnitOptions(): TimeUnit[] {
    return [TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];
  }

  public getDateRangeTypeDisplaySections(selectedType): Section[] {
    const items = this.getDateRangeTypeOptions().map((type) => ({
      value: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      label: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      isSelected: type === selectedType,
    }));
    return [{items}];
  }

  public getAttributes(): DateRangeAttributes {
    return pick(this, [`type`, `from`, `to`, `window`, `rolling`]);
  }

  public mergeAttributes(attributes: Partial<DateRangeAttributes>): this {
    // Use of merge has potential security issues, since we are using lodash >= 4.17.5 it is safe
    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    return new (this.constructor as any)(merge(this.getAttributes(), attributes));
  }

  public equals(otherRange: DateRange): boolean {
    return (
      this.type === otherRange.type &&
      isEqual(this.from, otherRange.from) &&
      isEqual(this.to, otherRange.to) &&
      isEqual(this.window, otherRange.window) &&
      this.rolling === otherRange.rolling
    );
  }

  public isValid(maxHistoryDays?: number): boolean {
    if (maxHistoryDays) {
      return this.getIntervalDays() <= maxHistoryDays;
    }

    return this._isValid();
  }

  public isWithinMaxHistoryLimit(maxHistoryDays: number): boolean {
    if (!this.isValid()) {
      return false;
    }
    if (maxHistoryDays === FEATURE_GATES_UNLIMITED) {
      return true;
    }

    let fromMoment = null;
    if (this.isRelativeWindow()) {
      const {value, unit} = this.window;
      assertIsStandardTimeUnit(unit);
      fromMoment = getCurrentMoment({localized: true}).subtract(value, unit);
    } else {
      fromMoment = (SPECIAL_DATE_RESOLVERS[this.from as string] || (() => moment(this.from as string)))();
    }
    const minDayMoment = getCurrentMoment({localized: true}).subtract(maxHistoryDays, `days`);
    return fromMoment.isSameOrAfter(minDayMoment);
  }

  public isValidRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && isNumber(value) && value >= 0;
  }

  public isValidRollingRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && unit === TimeUnit.Day && isNumber(value) && Math.abs(value) >= 0;
  }

  public isValidAbsoluteDate(date: string): boolean {
    return date in SPECIAL_DATE_RESOLVERS || !isNil(parseDate(date));
  }

  /**
   * Checks if this date range has time specified.
   *
   * @returns {boolean} true if this date range has hours, minutes, or seconds specified.
   */
  public hasSubDayGranularity(): boolean {
    return DateRange.hasSubDayGranularity(this);
  }

  public getCohortApiFormat(): ApiDateRange {
    // Cohorts has a slightly different format for relative before. this should work for other APIs as well.
    switch (this.type) {
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter: {
        return {window: this.window};
      }
      case DateRangeType.RelativeBefore: {
        return {to_date: this.window};
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        return {
          from_date: this.from,
          to_date: this.to,
        };
      }
      case DateRangeType.Since: {
        return {from_date: this.from};
      }
      case DateRangeType.Before: {
        return {to_date: this.to};
      }
      default: {
        return null;
      }
    }
  }

  public getCalendarMaxDate(): string | null {
    return null;
  }

  public isRelativeWindow(): boolean {
    return [
      DateRangeType.RelativeAfter,
      DateRangeType.NotRelativeAfter,
      DateRangeType.RelativeBefore,
      DateRangeType.RelativeFuture,
    ].includes(this.type);
  }

  public getCalendarDate(date: BaseDate): string {
    if (this.isRelativeWindow() || !date) {
      return null;
    }

    if (this.rolling) {
      return DateRange.convertRelativeDateToCalendarDate(date as RelativeDate);
    } else if (typeof date === `string` && date in SPECIAL_DATE_RESOLVERS) {
      const value = [START_OF_YESTERDAY, END_OF_YESTERDAY].includes(date) ? 1 : 0;
      return DateRange.convertRelativeDateToCalendarDate({unit: TimeUnit.Day, value});
    }
    return date as string;
  }

  public getCalendarFromDate(): string {
    return this.getCalendarDate(this.from);
  }

  public getCalendarToDate(): string {
    return this.getCalendarDate(this.to);
  }

  /** Returns a copy of this date range, clamped to fit within limits.
   *
   * @param limit - A pair of dates, setting a minimum and maximum date for the range.
   */
  public clamped(limit: [MomentInput, MomentInput]) {
    if (!this.from || !this.to) {
      throw new Error(`Only date ranges can be clamped`);
    }
    const formatToUse = this.hasSubDayGranularity() ? TIMESTAMP_FORMAT : CALENDAR_DATE_FORMAT;
    const updated = clampDateRange({
      from: this.getCalendarFromDate(),
      to: this.getCalendarToDate(),
      limit,
      format: formatToUse,
    });

    return new DateRange({
      ...this.getAttributes(),
      from: updated.from.format(formatToUse),
      to: updated.to.format(formatToUse),
    });
  }

  public isRolling(): boolean {
    return !!this.rolling || [START_OF_TODAY, START_OF_YESTERDAY].includes(this.from as string);
  }

  // TODO opslead
  public getLabel(): string {
    let parts = [];
    switch (this.type) {
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        const dates = [this.from, this.to];
        if (this.from === START_OF_TODAY && this.to === END_OF_TODAY) {
          parts = [`today`];
        } else if (this.from === START_OF_YESTERDAY && this.to === END_OF_YESTERDAY) {
          parts = [`yesterday`];
        } else {
          if (this.rolling) {
            const [first, second] = dates.map((date) => {
              return formatDate(parseDate(DateRange.convertRelativeDateToCalendarDate(date as RelativeDate)));
            });
            parts = [this.type, first, `and`, second, `(rolling)`];
          } else {
            const [first, second] = dates.map((date) => formatDate(parseDate(date as string)));
            parts = [this.type, first, `and`, second];
          }
        }
        break;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since:
        if (this.rolling) {
          const date = formatDate(parseDate(DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate)));
          parts = [this.type, date, `(rolling)`];
        } else {
          parts = [this.type, formatDate(parseDate(this.from as string))];
        }
        break;
      case DateRangeType.RelativeAfter:
      case DateRangeType.RelativeBefore: {
        const window = this.window;
        parts = [this.type, `${window.value > 1 ? `${window.value} ` : ``}${pluralize(window.unit, window.value)}`];
        break;
      }
      default:
        break;
    }
    return parts.join(` `);
  }

  // TODO: deprecated and should be removed, new code should use getDateRangeLabel() from common/widgets/date-range-picker-new/util to show the same label that the standard date range picker shows
  // get a display name in the same format as the date range presets
  public getDisplayText(): Translatable {
    switch (this.type) {
      case DateRangeType.StartsOn:
        return `Starting ${this.from}`;
      case DateRangeType.EndsOn:
        return `Ending ${this.to}`;
      case DateRangeType.RelativeAfter: {
        return `Last ${this.window.value} ${pluralize(this.window.unit, this.window.value)}`;
      }
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        if (this.from === START_OF_TODAY && this.to === END_OF_TODAY) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Today];
        } else if (this.from === START_OF_YESTERDAY && this.to === END_OF_YESTERDAY) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Yesterday];
        }
        if (this.rolling) {
          const from = DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate);
          const to = DateRange.convertRelativeDateToCalendarDate(this.to as RelativeDate);
          return `${formatDate(parseDate(from))} — ${formatDate(parseDate(to))} (rolling)`;
        }
        return `${formatDate(parseDate(this.from as string))} — ${formatDate(parseDate(this.to as string))}`;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since: {
        // On or Since
        if (this.from === START_OF_TODAY && !this.to) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Today];
        } else if (this.from === START_OF_WEEK && !this.to) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.WeekToDate];
        } else if (this.from === START_OF_MONTH && !this.to) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.MonthToDate];
        } else if (this.from === START_OF_QUARTER && !this.to) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.QuarterToDate];
        } else if (this.from === START_OF_YEAR && !this.to) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.YearToDate];
        }
        if (this.rolling) {
          const from = DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate);
          return `${capitalize(this.type)} ${formatDate(parseDate(from))} (rolling)`;
        }
        return `${capitalize(this.type)} ${formatDate(parseDate(this.from as string))}`;
      }
      default:
        // note: RelativeBefore is not supported
        return this.getLabel();
    }
  }

  private _isValid(): boolean {
    if (!this.getDateRangeTypeOptions().includes(this.type)) {
      return false;
    }

    switch (this.type) {
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since: {
        if (this.rolling) {
          return this.isValidRollingRelativeDate(this.from as RelativeDate);
        }

        return this.isValidAbsoluteDate(this.from as string);
      }
      case DateRangeType.Before:
        return this.isValidAbsoluteDate(this.to as string);
      case DateRangeType.Between:
      case DateRangeType.NotBetween:
        if (this.rolling) {
          return (
            this.isValidRollingRelativeDate(this.from as RelativeDate) &&
            this.isValidRollingRelativeDate(this.to as RelativeDate)
          );
        }
        return this.isValidAbsoluteDate(this.from as string) && this.isValidAbsoluteDate(this.to as string);
      case DateRangeType.RelativeFuture:
      case DateRangeType.RelativeBefore:
      case DateRangeType.RelativeAfter:
      case DateRangeType.NotRelativeAfter: {
        return this.isValidRelativeDate(this.window);
      }
      default:
        return false;
    }
  }
}
